import * as React from "react";
import { Box, Container, Typography } from "@mui/material";
import { MuiTelInput } from "mui-tel-input";
import type { MuiTelInputInfo } from "mui-tel-input";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

type TelProps = {
    value: string;
    onChangeValue: (value: string, info?: MuiTelInputInfo) => void;
} & Record<string, any>;

export function TelInputWithClickableCallingCode({
    value,
    onChangeValue,
    ...rest
}: TelProps) {
    const rootRef = React.useRef<HTMLDivElement | null>(null);
    const [startWidth, setStartWidth] = React.useState<number>(0);

    const openDropdown = React.useCallback(() => {
        const root = rootRef.current;
        if (!root) return;

        const btn =
            (root.querySelector('button[aria-label*="country"]') as HTMLButtonElement | null) ??
            (root.querySelector("button.MuiTelInput-IconButton") as HTMLButtonElement | null);

        if (!btn) return;

        // Prevent �focus causes scroll� in some containers/browsers
        btn.focus?.({ preventScroll: true } as any);

        btn.click();
    }, []);


    React.useLayoutEffect(() => {
        const root = rootRef.current;
        if (!root) return;

        const update = () => {
            const adorn = root.querySelector(".MuiInputAdornment-positionStart") as HTMLElement | null;
            if (!adorn) return;
            setStartWidth(adorn.getBoundingClientRect().width);
        };

        update();
        const ro = new ResizeObserver(update);
        ro.observe(root);
        window.addEventListener("resize", update);

        return () => {
            ro.disconnect();
            window.removeEventListener("resize", update);
        };
    }, []);

    return (
        <Box ref={rootRef} sx={{ position: "relative", width: "100%" }}>
            <MuiTelInput
                {...rest}
                value={value}
                onChange={(newValue: string, info: MuiTelInputInfo) => {
                    onChangeValue(info?.numberValue ?? newValue, info);
                }}
                // Main fix for MUI menu scroll jumps:
                MenuProps={{ disableScrollLock: true }}
                sx={{
                    "& .MuiInputAdornment-positionStart": {
                        cursor: "pointer",
                        borderRight: "1px solid",
                        borderColor: "divider",
                        pr: 0.75,
                        mr: 0.75,
                        pl: 3,
                        borderTopLeftRadius: 6,
                        borderBottomLeftRadius: 6,
                    },
                    "& .MuiInputAdornment-positionStart:hover": {
                        backgroundColor: "action.hover",
                    },
                    ...(rest.sx ?? {}),
                }}
            />

            <KeyboardArrowDownIcon
                sx={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: 18,
                    opacity: 0.65,
                    pointerEvents: "none", // overlay below handles clicks
                    zIndex: 2,
                }}
            />

            {startWidth > 0 && (
                <Box
                    role="button"
                    tabIndex={0}
                    aria-label="Change country"
                    onPointerDown={(e) => {
                        // Prevent focus/selection quirks and make it feel like a button
                        e.preventDefault();
                        e.stopPropagation();
                        openDropdown();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            openDropdown();
                        }
                    }}
                    sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        height: "100%",
                        width: startWidth,
                        cursor: "pointer",
                        zIndex: 3,
                        backgroundColor: "transparent",
                        borderTopLeftRadius: 6,
                        borderBottomLeftRadius: 6,
                    }}
                />
            )}
        </Box>
    );
}
export default function PhoneInputPage() {
    const [value, setValue] = React.useState<string>("");

    const showError = value.length > 0 && value.length < 6;
    const helperText = showError ? "Please enter a valid phone number" : " ";

    return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                Phone number
            </Typography>

            <TelInputWithClickableCallingCode
                value={value}
                onChangeValue={(newValue) => setValue(newValue)}
                defaultCountry={"IE"}
                preferredCountries={["IE", "GB"]}
                forceCallingCode
                fullWidth
                disabled={false}
                label="Mobile"
                error={showError}
                helperText={helperText}
                inputProps={{
                    "data-ext-ignore": "true",
                    "data-nuacom-ignore": "true",
                    inputMode: "tel",
                }}
            />

            <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Stored value:
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                    {value || "(empty)"}
                </Typography>
            </Box>
        </Container>
    );
}
